import {
    Hash,
    P2PKH,
    PrivateKey,
    TransactionSignature,
    UnlockingScript,
    Utils,
} from '@treechat/bsv-sdk'

const SIGHASH_ALL_FORKID = 0x41
const MAP_PREFIX = '1PuQa7K62MiKCtssSLKy1kh56WWU7MtUR5'
const HEX_32 = /^[0-9a-f]{64}$/i
const CONTENT_CHUNK_BYTES = 64 * 1024
export const LOCAL_ORDINAL_MAX_PREVOUT_SCRIPT_BYTES = 8 * 1024 * 1024
const encoder = new TextEncoder()

export type MintReadMetrics = {
    bytes: number
    chunks: number
    read_ms: number
    hash_ms: number
    yield_ms: number
}
export type MintReadObserver = (metrics: MintReadMetrics) => void
const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now())

export type MintContentSource = {
    mode?: 'local_file' | 'remote_chunks'
    size: number
    type: string
    name?: string
    readChunk: (start: number, end: number) => Promise<Uint8Array>
}

type MintInput = {
    txid: string
    vout: number
    satoshis: number
    scriptPubKey: string
    sequence: number
    role: 'payment'
}

type MintOutput = {
    vout: number
    satoshis: number
    scriptBytes: number
    scriptSha256: string
    script?: string
}

export type OrdinalMintManifest = {
    version: 1
    operation: 'ordinal_mint'
    signingRequestId: string
    attemptId: string
    userId: string
    deviceId: string
    answerId: string
    attachmentId: string
    walletGeneration: string
    txVersion: number
    lockTime: number
    paymentAddress: string
    ordinalAddress: string
    contentType: string
    metadata: { app: string; type: 'ord'; filename?: string }
    contentBytes: number
    contentSha256: string
    inputs: MintInput[]
    outputs: MintOutput[]
    ordinalVout: 0
    serviceFeeVout: 1
    serviceFeeSatoshis: number
    networkFeeSatoshis: number
    changeSatoshis: number
}

export type OrdinalMintIntent = {
    version: 1
    userId: string
    deviceId: string
    answerId: string
    attachmentId: string
    blobId: string
    paymentAddress: string
    ordinalAddress: string
    contentType: string
    contentBytes: number
    contentSha256: string
    app: string
    filename?: string
    serviceFeeAddress: string
    serviceFeeSatoshis: number
    maxNetworkFeeSatoshis: number
    createdAt: number
}

export type OrdinalMintSignature = {
    manifestDigest: string
    inputIndex: number
    txid: string
    vout: number
    unlockingScriptHex: string
}

function bytes(hex: string): Uint8Array {
    if (hex.length % 2 !== 0 || !/^[0-9a-f]*$/i.test(hex)) throw new Error('Invalid hex')
    return Uint8Array.from(hex.match(/../g)?.map(byte => Number.parseInt(byte, 16)) ?? [])
}

function hex(value: ArrayLike<number>): string {
    return Array.from(value, byte => byte.toString(16).padStart(2, '0')).join('')
}

function sha256(value: ArrayLike<number>): Uint8Array {
    return Uint8Array.from(new Hash.SHA256().update(value).digest())
}

function hash256(value: ArrayLike<number>): Uint8Array {
    return sha256(sha256(value))
}

function safeInteger(value: number, minimum = 0): boolean {
    return Number.isSafeInteger(value) && value >= minimum
}

function readWithFileReader(blob: Blob): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onerror = () => reject(reader.error || new Error('Unable to read mint content'))
        reader.onload = () => {
            if (!(reader.result instanceof ArrayBuffer)) {
                reject(new Error('Mint content reader returned an unsupported result'))
                return
            }
            resolve(new Uint8Array(reader.result))
        }
        reader.readAsArrayBuffer(blob)
    })
}

async function readBlobChunk(blob: Blob, start: number, end: number): Promise<Uint8Array> {
    const slice = blob.slice(start, end)
    if (typeof slice.arrayBuffer === 'function') {
        return new Uint8Array(await slice.arrayBuffer())
    }
    if (typeof FileReader !== 'undefined') return readWithFileReader(slice)
    throw new Error('This browser cannot read mint content in bounded chunks')
}

export function blobMintContentSource(blob: Blob, name?: string): MintContentSource {
    return {
        mode: 'local_file',
        size: blob.size,
        type: blob.type,
        name: name || (typeof File !== 'undefined' && blob instanceof File ? blob.name : undefined),
        readChunk: (start, end) => readBlobChunk(blob, start, end),
    }
}

async function yieldToBrowser(): Promise<void> {
    await new Promise<void>(resolve => {
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => resolve())
        else setTimeout(resolve, 0)
    })
}

async function forEachContentChunk(
    source: MintContentSource,
    consume: (chunk: Uint8Array) => void,
    observe?: MintReadObserver,
): Promise<number> {
    const metrics: MintReadMetrics = { bytes: 0, chunks: 0, read_ms: 0, hash_ms: 0, yield_ms: 0 }
    let lastProgress = now()
    try {
        let readBytes = 0
        let chunkIndex = 0
        for (let offset = 0; offset < source.size; offset += CONTENT_CHUNK_BYTES) {
            const expectedEnd = Math.min(offset + CONTENT_CHUNK_BYTES, source.size)
            const readStart = now()
            let chunk: Uint8Array
            try {
                chunk = await source.readChunk(offset, expectedEnd)
            } finally {
                metrics.read_ms += now() - readStart
            }
            metrics.bytes += chunk.length
            metrics.chunks++
            if (chunk.length !== expectedEnd - offset)
                throw new Error('Mint content chunk is incomplete')
            readBytes += chunk.length
            if (readBytes > source.size) throw new Error('Mint content is longer than expected')
            const hashStart = now()
            try {
                consume(chunk)
            } finally {
                metrics.hash_ms += now() - hashStart
            }
            if (now() - lastProgress >= 5000) {
                try {
                    observe?.({ ...metrics })
                } catch {}
                lastProgress = now()
            }
            // Keep hashing/signing responsive in iOS and Android WebViews, where a long
            // chain of already-resolved promises may otherwise monopolize the UI thread.
            if (++chunkIndex % 4 === 0) {
                const yieldStart = now()
                await yieldToBrowser()
                metrics.yield_ms += now() - yieldStart
            }
        }
        return readBytes
    } finally {
        try {
            observe?.(metrics)
        } catch {
            /* Diagnostics cannot affect signing. */
        }
    }
}

export async function hashMintContentSource(
    source: MintContentSource,
    observe?: MintReadObserver,
): Promise<string> {
    const hash = new Hash.SHA256()
    const readBytes = await forEachContentChunk(source, chunk => hash.update(chunk), observe)
    if (readBytes !== source.size) throw new Error('Mint content size changed while reading')
    return hex(hash.digest())
}

function canonicalize(value: unknown): unknown {
    if (Array.isArray(value)) return value.map(canonicalize)
    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>)
                .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
                .map(([key, entry]) => [key, canonicalize(entry)]),
        )
    }
    return value
}

function pushPrefix(length: number): Uint8Array {
    if (!safeInteger(length) || length >= 2 ** 32) throw new Error('Unsupported pushdata length')
    if (length === 0) return Uint8Array.of(0)
    if (length < 76) return Uint8Array.of(length)
    if (length < 256) return Uint8Array.of(76, length)
    if (length < 65536) return Uint8Array.of(77, length & 255, length >>> 8)
    return Uint8Array.of(
        78,
        length & 255,
        (length >>> 8) & 255,
        (length >>> 16) & 255,
        length >>> 24,
    )
}

function pushed(value: Uint8Array): Uint8Array {
    const prefix = pushPrefix(value.length)
    const result = new Uint8Array(prefix.length + value.length)
    result.set(prefix)
    result.set(value, prefix.length)
    return result
}

function scriptSize(
    contentBytes: number,
    contentType: string,
    ordinalTail: Uint8Array,
    metadata: OrdinalMintManifest['metadata'],
): number {
    const values = Object.entries(metadata).flatMap(([key, value]) => [
        encoder.encode(key),
        encoder.encode(value),
    ])
    const mapBytes = [encoder.encode(MAP_PREFIX), encoder.encode('SET'), ...values]
    return (
        2 +
        pushed(encoder.encode('ord')).length +
        1 +
        pushed(encoder.encode(contentType)).length +
        1 +
        pushPrefix(contentBytes).length +
        contentBytes +
        1 +
        ordinalTail.length +
        1 +
        mapBytes.reduce((sum, value) => sum + pushed(value).length, 0)
    )
}

export function ordinalMintLockingScriptBytes(options: {
    contentBytes: number
    contentType: string
    filename?: string
    app: string
}): number {
    return scriptSize(options.contentBytes, options.contentType, new Uint8Array(25), {
        app: options.app,
        type: 'ord',
        ...(options.filename ? { filename: options.filename } : {}),
    })
}

async function hashMintOutputs(
    source: MintContentSource,
    manifest: OrdinalMintManifest,
    ordinalTail: Uint8Array,
    observe?: MintReadObserver,
): Promise<Uint8Array> {
    const contentHash = new Hash.SHA256()
    const scriptHash = new Hash.SHA256()
    const outputsHash = new Hash.SHA256()
    const first = manifest.outputs[0]
    const outputHeader = new Utils.Writer()
    outputHeader.writeUInt64LE(first.satoshis)
    outputHeader.writeVarIntNum(first.scriptBytes)
    outputsHash.update(outputHeader.toUint8Array())
    const feedScript = (chunk: Uint8Array) => {
        scriptHash.update(chunk)
        outputsHash.update(chunk)
    }
    feedScript(Uint8Array.of(0, 99)) // OP_0 OP_IF
    feedScript(pushed(encoder.encode('ord')))
    feedScript(Uint8Array.of(81)) // OP_1
    feedScript(pushed(encoder.encode(manifest.contentType)))
    feedScript(Uint8Array.of(0))
    feedScript(pushPrefix(source.size))
    const readBytes = await forEachContentChunk(
        source,
        chunk => {
            contentHash.update(chunk)
            feedScript(chunk)
        },
        observe,
    )
    if (
        readBytes !== manifest.contentBytes ||
        hex(contentHash.digest()) !== manifest.contentSha256
    ) {
        throw new Error('Mint content does not match the manifest')
    }
    feedScript(Uint8Array.of(104)) // OP_ENDIF
    feedScript(ordinalTail)
    feedScript(Uint8Array.of(106)) // OP_RETURN
    for (const value of [
        MAP_PREFIX,
        'SET',
        ...Object.entries(manifest.metadata).flatMap(pair => pair),
    ]) {
        feedScript(pushed(encoder.encode(value)))
    }
    if (hex(scriptHash.digest()) !== first.scriptSha256) {
        throw new Error('Mint inscription script does not match the manifest')
    }
    for (const output of manifest.outputs.slice(1)) {
        const outputBytes = bytes(output.script!)
        const header = new Utils.Writer()
        header.writeUInt64LE(output.satoshis)
        header.writeVarIntNum(outputBytes.length)
        outputsHash.update(header.toUint8Array())
        outputsHash.update(outputBytes)
    }
    return sha256(outputsHash.digest())
}

function assertManifest(
    manifest: OrdinalMintManifest,
    intent: OrdinalMintIntent,
    source: MintContentSource,
): void {
    if (
        manifest.version !== 1 ||
        manifest.operation !== 'ordinal_mint' ||
        !manifest.signingRequestId ||
        !manifest.attemptId
    ) {
        throw new Error('Invalid mint signing request')
    }
    if (
        manifest.userId !== intent.userId ||
        manifest.deviceId !== intent.deviceId ||
        manifest.answerId !== intent.answerId ||
        manifest.attachmentId !== intent.attachmentId
    )
        throw new Error('Mint request has a different user or device')
    if (
        manifest.paymentAddress !== intent.paymentAddress ||
        manifest.ordinalAddress !== intent.ordinalAddress
    )
        throw new Error('Mint request has a different wallet')
    if (
        manifest.contentType !== intent.contentType ||
        manifest.metadata.app !== intent.app ||
        manifest.metadata.type !== 'ord' ||
        manifest.metadata.filename !== intent.filename
    )
        throw new Error('Mint request content terms changed')
    if (Object.keys(manifest.metadata).some(key => !['app', 'type', 'filename'].includes(key)))
        throw new Error('Unsupported mint metadata')
    if (
        manifest.contentBytes !== source.size ||
        manifest.contentBytes !== intent.contentBytes ||
        manifest.contentSha256 !== intent.contentSha256 ||
        !HEX_32.test(manifest.contentSha256)
    )
        throw new Error('Mint content size or hash is invalid')
    if (
        manifest.ordinalVout !== 0 ||
        manifest.serviceFeeVout !== 1 ||
        manifest.txVersion !== 1 ||
        manifest.lockTime !== 0
    )
        throw new Error('Unsupported mint transaction layout')
    if (
        !safeInteger(manifest.networkFeeSatoshis, 1) ||
        manifest.networkFeeSatoshis > intent.maxNetworkFeeSatoshis
    )
        throw new Error('Mint miner fee exceeds the locally authorized limit')
    if (
        !safeInteger(manifest.serviceFeeSatoshis, 1) ||
        manifest.serviceFeeSatoshis !== intent.serviceFeeSatoshis
    )
        throw new Error('Mint service fee changed')
    if (!manifest.inputs.length || manifest.outputs.length < 2 || manifest.outputs.length > 3)
        throw new Error('Unsupported mint input or output count')
    const paymentScript = new P2PKH().lock(intent.paymentAddress).toHex()
    const ordinalScript = new P2PKH().lock(intent.ordinalAddress).toBinary()
    if (
        manifest.outputs[0].vout !== 0 ||
        manifest.outputs[0].satoshis !== 1 ||
        manifest.outputs[0].script ||
        manifest.outputs[0].scriptBytes !==
            scriptSize(
                source.size,
                manifest.contentType,
                Uint8Array.from(ordinalScript),
                manifest.metadata,
            )
    )
        throw new Error('Mint ordinal output changed')
    const serviceScript = new P2PKH().lock(intent.serviceFeeAddress).toHex()
    if (
        manifest.outputs[1].vout !== 1 ||
        manifest.outputs[1].satoshis !== intent.serviceFeeSatoshis ||
        manifest.outputs[1].script?.toLowerCase() !== serviceScript.toLowerCase()
    )
        throw new Error('Mint service output changed')
    if (
        manifest.outputs.length === 3 &&
        (manifest.outputs[2].vout !== 2 ||
            manifest.outputs[2].script?.toLowerCase() !== paymentScript.toLowerCase() ||
            manifest.outputs[2].satoshis !== manifest.changeSatoshis)
    )
        throw new Error('Mint change output changed')
    if (manifest.outputs.length === 2 && manifest.changeSatoshis !== 0)
        throw new Error('Mint change amount changed')
    for (const [index, input] of manifest.inputs.entries()) {
        if (
            !HEX_32.test(input.txid) ||
            !safeInteger(input.vout) ||
            !safeInteger(input.satoshis, 1) ||
            input.role !== 'payment' ||
            input.sequence !== 0xffffffff ||
            input.scriptPubKey.toLowerCase() !== paymentScript.toLowerCase()
        )
            throw new Error(`Invalid mint payment input ${index}`)
        if (
            manifest.inputs.findIndex(
                candidate => candidate.txid === input.txid && candidate.vout === input.vout,
            ) !== index
        )
            throw new Error('Duplicate mint payment input')
    }
    for (const output of manifest.outputs.slice(1)) {
        if (
            !safeInteger(output.satoshis, 1) ||
            !output.script ||
            bytes(output.script).length !== output.scriptBytes ||
            hex(sha256(bytes(output.script))) !== output.scriptSha256
        )
            throw new Error('Invalid mint payment output')
    }
    const inputTotal = manifest.inputs.reduce((sum, input) => sum + input.satoshis, 0)
    const outputTotal = manifest.outputs.reduce((sum, output) => sum + output.satoshis, 0)
    if (
        !safeInteger(inputTotal) ||
        !safeInteger(outputTotal) ||
        inputTotal - outputTotal !== manifest.networkFeeSatoshis
    )
        throw new Error('Mint miner fee differs from inputs minus outputs')
}

export async function signOrdinalMint(
    manifest: OrdinalMintManifest,
    intent: OrdinalMintIntent,
    content: Blob | MintContentSource,
    paymentKey: PrivateKey | string,
    observe?: MintReadObserver,
): Promise<OrdinalMintSignature[]> {
    const source =
        'readChunk' in content ? content : blobMintContentSource(content, intent.filename)
    assertManifest(manifest, intent, source)
    const key = typeof paymentKey === 'string' ? PrivateKey.fromWif(paymentKey) : paymentKey
    if (key.toAddress() !== intent.paymentAddress) throw new Error('Wrong mint payment key')
    const expectedWalletGeneration = hex(
        sha256(encoder.encode(`${intent.paymentAddress}:${intent.ordinalAddress}`)),
    )
    if (manifest.walletGeneration !== expectedWalletGeneration)
        throw new Error('Mint wallet generation changed')
    const ordinalTail = Uint8Array.from(new P2PKH().lock(intent.ordinalAddress).toBinary())
    const hashOutputs = await hashMintOutputs(source, manifest, ordinalTail, observe)
    const prevouts = new Utils.Writer()
    const sequences = new Utils.Writer()
    for (const input of manifest.inputs) {
        prevouts.writeReverse(bytes(input.txid))
        prevouts.writeUInt32LE(input.vout)
        sequences.writeUInt32LE(input.sequence)
    }
    const hashPrevouts = hash256(prevouts.toUint8Array())
    const hashSequence = hash256(sequences.toUint8Array())
    const paymentScript = bytes(new P2PKH().lock(intent.paymentAddress).toHex())
    const manifestDigest = hex(sha256(encoder.encode(JSON.stringify(canonicalize(manifest)))))
    return manifest.inputs.map((input, inputIndex) => {
        const preimage = new Utils.Writer()
        preimage.writeInt32LE(manifest.txVersion)
        preimage.write(hashPrevouts)
        preimage.write(hashSequence)
        preimage.writeReverse(bytes(input.txid))
        preimage.writeUInt32LE(input.vout)
        preimage.writeVarIntNum(paymentScript.length)
        preimage.write(paymentScript)
        preimage.writeUInt64LE(input.satoshis)
        preimage.writeUInt32LE(input.sequence)
        preimage.write(hashOutputs)
        preimage.writeUInt32LE(manifest.lockTime)
        preimage.writeUInt32LE(SIGHASH_ALL_FORKID)
        const rawSignature = key.sign(Array.from(sha256(preimage.toUint8Array())))
        const signature = new TransactionSignature(
            rawSignature.r,
            rawSignature.s,
            SIGHASH_ALL_FORKID,
        )
        const script = new UnlockingScript([
            { op: signature.toChecksigFormat().length, data: signature.toChecksigFormat() },
            { op: 33, data: key.toPublicKey().encode(true) as number[] },
        ])
        return {
            manifestDigest,
            inputIndex,
            txid: input.txid,
            vout: input.vout,
            unlockingScriptHex: script.toHex(),
        }
    })
}
